// src/components/InclusionPanel.js - RULING 305.12 (Record 652; ruling 305.6). The member's "ask to be included" button and
// the officers' answer (include / decline with a reason), with Bob's caution in front of the officer. Decisions are in
// src\data\inclusion.js; firestore.rules decides who may write what.
import React, { useState } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { claimPatch } from '../data/claimWrite';
import { inclusionState, askPatch, decisionPatch, OFFICER_CAUTION, MEMBER_ASK_TEXT, MEMBER_ASK_EXPLAIN } from '../data/inclusion';
import { isClaimantOf } from './ClaimFormActions';

const C = { line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };
const isOfficer = (u) => !!u && (u.isAdmin === true || ['admin', 'local_chair', 'general_chair'].includes(String(u.role || '')));

export default function InclusionPanel({ claimId, claim, user, onPatched }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [reason, setReason] = useState('');
  if (!claim || String(claim.status || '') === 'draft') return null;
  const st = inclusionState(claim);
  const officer = isOfficer(user);
  const claimant = !officer && isClaimantOf(claim, user);
  if (!officer && !claimant) return null;
  const write = async (r) => {
    if (!r.ok) { setMsg(r.errors.join(' ')); return; }
    setBusy(true); setMsg('');
    try {
      await updateDoc(doc(db, 'claims', claimId), claimPatch({ ...r.patch, updatedAt: serverTimestamp() }, user));
      setMsg('Recorded.'); setReason('');
      if (onPatched) onPatched(r.patch);
    } catch (e) { setMsg('NOT recorded: ' + ((e && (e.code || e.message)) || 'refused') + '.'); }
    finally { setBusy(false); }
  };
  const box = { border: '1px solid ' + (st === 'asked' && officer ? C.accent : C.line), borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: C.dim, lineHeight: 1.5 };
  if (claimant) {
    return (
      <div style={box}>
        {st === 'included' ? <div style={{ color: C.ok }}>Your representatives have included you in their discussions of this claim.</div>
          : st === 'asked' ? <div>You asked to be included in your representatives' discussions. Their answer will show here.</div>
          : (
            <div>
              {st === 'declined' ? <div style={{ marginBottom: 6 }}>Your earlier request was declined: {String((claim.inclusionDecision || {}).reason || '')}</div> : null}
              <div style={{ marginBottom: 6 }}>{MEMBER_ASK_EXPLAIN}</div>
              <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => write(askPatch(claim, user, serverTimestamp()))}>{MEMBER_ASK_TEXT}</button>
            </div>
          )}
        {msg ? <div style={{ marginTop: 6, color: /^NOT|^Your|^A draft/.test(msg) ? C.bad : C.ok }}>{msg}</div> : null}
      </div>
    );
  }
  return (
    <div style={box}>
      {st === 'included' ? <div>The member is included in the officers' discussions of this claim: he reads every officer note as it is written.</div> : null}
      {st === 'asked' ? (
        <div>
          <div style={{ color: C.accent, fontWeight: 700 }}>The member has asked to be included in the officers' discussions of this claim.</div>
          <div style={{ margin: '4px 0 8px' }}>{OFFICER_CAUTION}</div>
          <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => write(decisionPatch(claim, user, true, '', serverTimestamp()))} style={{ marginRight: 8 }}>Include him</button>
          <input aria-label="If you decline: why" className="form-input" placeholder="If you decline: why" value={reason} onChange={(e) => setReason(e.target.value)} style={{ fontSize: 12, width: 260, marginRight: 8 }} />
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => write(decisionPatch(claim, user, false, reason, serverTimestamp()))}>Decline</button>
        </div>
      ) : null}
      {st === 'none' || st === 'declined' ? (
        <div>
          <span>The member is not included in the officers' discussions{st === 'declined' ? ' (a request was declined)' : ''}. </span>
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => write(decisionPatch(claim, user, true, '', serverTimestamp()))}>Invite the member in</button>
        </div>
      ) : null}
      {msg ? <div style={{ marginTop: 6, color: /^NOT|^Only|^Say|^There|^The member is already/.test(msg) ? C.bad : C.ok }}>{msg}</div> : null}
    </div>
  );
}
